import { useCallback, useLayoutEffect, useMemo, useRef, type ReactNode } from 'react';
import { useIntersectionObserver } from '@/core/hooks/useIntersectionObserver';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import {
  HOME_PAGE_SECTION_VIEWED,
  newHomeSectionViewedEventProperties,
  resolveNewHomeSectionViewedSectionName,
} from '../analytics/homePageSectionViewed';

/** Passed to `IntersectionObserver` (`useIntersectionObserver` `threshold`). Tune freely. */
const HOME_PAGE_SECTION_VIEWED_IO_THRESHOLDS: number[] = [0, 0.2, 0.25, 0.5, 0.75, 1];

/** Section must be at least this visible (0–1) to fire the event. */
const HOME_PAGE_SECTION_VIEWED_MIN_INTERSECTION_RATIO = 0.3;
const HOME_PAGE_CATEGORY_GRID_SECTION_VIEWED_MIN_INTERSECTION_RATIO = 0.2;
const HOME_PAGE_PLP_SECTION_VIEWED_MIN_INTERSECTION_RATIO = 0.0000001;

type HomePageSectionViewTrackerProps = {
  sectionKey: string;
  viewType: string;
  children: ReactNode;
};

export default function HomePageSectionViewTracker({
  sectionKey,
  viewType,
  children,
}: HomePageSectionViewTrackerProps) {
  const sectionName = useMemo(
    () => resolveNewHomeSectionViewedSectionName(sectionKey, viewType),
    [sectionKey, viewType],
  );

  const { analytics, isInitialized } = useAnalytics();
  const analyticsRef = useRef(analytics);
  const isInitRef = useRef(isInitialized);

  const hasTrackedRef = useRef(false);
  const sectionNameRef = useRef<string | null>(sectionName);

  useLayoutEffect(() => {
    analyticsRef.current = analytics;
    isInitRef.current = isInitialized;
    sectionNameRef.current = sectionName;
  }, [analytics, isInitialized, sectionName]);

  const observerEnabled = Boolean(sectionName) && isInitialized && Boolean(analytics);

  const onIntersect = useCallback(
    (entry: IntersectionObserverEntry) => {
      if (hasTrackedRef.current) return;
      const minRatio =
        sectionKey === 'recommended'
          ? HOME_PAGE_PLP_SECTION_VIEWED_MIN_INTERSECTION_RATIO
          : sectionKey === 'categoryGrid'
            ? HOME_PAGE_CATEGORY_GRID_SECTION_VIEWED_MIN_INTERSECTION_RATIO
            : HOME_PAGE_SECTION_VIEWED_MIN_INTERSECTION_RATIO;
      if (!entry.isIntersecting || entry.intersectionRatio < minRatio) return;
      const name = sectionNameRef.current;
      if (!name) return;
      const a = analyticsRef.current;
      if (!a || !isInitRef.current) return;
      hasTrackedRef.current = true;
      try {
        a.trackEvent(HOME_PAGE_SECTION_VIEWED, newHomeSectionViewedEventProperties(name));
      } catch (err) {
        hasTrackedRef.current = false;
        console.error(`Error tracking "${HOME_PAGE_SECTION_VIEWED}":`, err);
      }
    },
    [sectionKey],
  );

  const { ref } = useIntersectionObserver<HTMLDivElement>(
    { threshold: HOME_PAGE_SECTION_VIEWED_IO_THRESHOLDS, enabled: observerEnabled },
    onIntersect,
  );

  if (!sectionName) {
    return children;
  }

  return (
    <div ref={ref} className="w-full min-w-0">
      {children}
    </div>
  );
}
